import type { Object3D } from 'three';
import { isPointCoincidentConstraint, PointCoincidentConstraint } from '../../../sketch/solver/constraint/relationDefinition/point/PointCoincidentConstraint';
import {
	isSketchArcObject,
	isSketchEllipseObject,
	isSketchLineObject,
	isSketchSlotObject,
	isSketchSplineObject,
	isSketchStyleSplineObject,
} from '../../../mesh/guards';
import type { SketchPointObject } from '../../../mesh/sketch/SketchPointObject';
import {
	resolveExternalGeometrySourceDescriptorKey,
	type ExternalGeometrySourceDescriptor,
} from '../model/ExternalGeometrySourceDescriptor';
import type { TargetAnchorBindings } from '../model/TargetAnchorBindings';
import type { SketchReferenceSourceResolverRuntime } from '../source/projection/SketchReferenceSourceResolver';
import { resolveSketchSlotEdgeByIndex } from './resolveSketchSlotEdgeByIndex';

export type { TargetAnchorBindings } from '../model/TargetAnchorBindings';

import type {
	SketchBasicConstraintRestoreInput,
	SketchBasicConstraintRestoreResult,
} from '../../../../types/reference/sketch-entity-reference/SketchBasicConstraintRestoreService';

export type {
	SketchBasicConstraintRestoreInput,
	SketchBasicConstraintRestoreResult,
} from '../../../../types/reference/sketch-entity-reference/SketchBasicConstraintRestoreService';

/**
 * 基础约束恢复服务。
 * 说明：
 * 1. 当前阶段只恢复 coincident；
 * 2. 面向存在端点语义的曲线源实体；
 * 3. 来源真实共享点已在目标创建阶段映射为共享端点；本服务只恢复不同来源点之间可删除的显式重合关系。
 */
export class SketchBasicConstraintRestoreService {
	/**
	 * 恢复同批次基础约束。
	 *
	 * @param input 恢复输入。
	 * @returns 恢复结果。
	 */
	public restore(input: SketchBasicConstraintRestoreInput): SketchBasicConstraintRestoreResult {
		if (input.options?.restoreBasicConstraints === false) {
			return {
				restoredConstraintIds: [],
				diagnostics: [],
			};
		}

		const restoredConstraintIds: number[] = [];
		const endpointOwnersByAnchorId = new Map<string, Set<string>>();
		const endpointObjectsByAnchorId = new Map<string, SketchPointObject>();
		const restoredAnchorGroupKeys = new Set<string>();

		input.sourceDescriptors.forEach(descriptor => {
			if (descriptor.kind !== 'sketch-entity') {
				return;
			}

			const endpointObjects = this.resolveEndpointObjectsFromDescriptor(descriptor, input.runtime);
			const sourceOwnerKey = resolveExternalGeometrySourceDescriptorKey(descriptor);
			endpointObjects.forEach(endpointObject => {
				const owners = endpointOwnersByAnchorId.get(endpointObject.uuid) ?? new Set<string>();
				owners.add(sourceOwnerKey);
				endpointOwnersByAnchorId.set(endpointObject.uuid, owners);
				endpointObjectsByAnchorId.set(endpointObject.uuid, endpointObject);
			});
		});

		const seenCoincidentConstraintIds = new Set<number>();
		endpointObjectsByAnchorId.forEach(endpointObject => {
			endpointObject.constraints.getRelations().forEach(constraint => {
				if (!isPointCoincidentConstraint(constraint) || seenCoincidentConstraintIds.has(constraint.id)) {
					return;
				}
				seenCoincidentConstraintIds.add(constraint.id);

				const inBatchAnchorIds = constraint
					.getTargetsSnapshot()
					.map(targetPoint => targetPoint.uuid)
					.filter((targetPointId, index, array) => endpointOwnersByAnchorId.has(targetPointId) && array.indexOf(targetPointId) === index);
				if (inBatchAnchorIds.length < 2) {
					return;
				}

				this.restoreCoincidentByAnchorIds(inBatchAnchorIds, input.targetAnchorBindings, restoredConstraintIds, restoredAnchorGroupKeys);
			});
		});

		return {
			restoredConstraintIds,
			diagnostics: [],
		};
	}

	/**
	 * 从 descriptor 解析当前阶段可恢复端点关系的源点。
	 *
	 * @param descriptor 来源描述。
	 * @param runtime 运行时依赖。
	 * @returns 命中时返回端点集合，否则返回空数组。
	 */
	private resolveEndpointObjectsFromDescriptor(
		descriptor: ExternalGeometrySourceDescriptor,
		runtime: SketchReferenceSourceResolverRuntime
	): SketchPointObject[] {
		if (descriptor.kind !== 'sketch-entity') {
			return [];
		}

		const sourceObject = runtime.getSketchObject(descriptor.sourceSketchId, descriptor.entityId);
		if (!sourceObject) {
			return [];
		}

		if (descriptor.subEntity?.kind === 'slot-edge') {
			if (!isSketchSlotObject(sourceObject)) {
				return [];
			}
			const sourceEdge = resolveSketchSlotEdgeByIndex(sourceObject, descriptor.subEntity.edgeIndex);
			return sourceEdge ? this.resolveEndpointObjects(sourceEdge) : [];
		}

		return this.resolveEndpointObjects(sourceObject);
	}

	/**
	 * 解析源实体的端点对象。
	 * 说明：
	 * 1. 端点对象 uuid 是批处理恢复共享拓扑的稳定 anchor；
	 * 2. 样条只取起终控制点，避免把内部控制柄误判为实体连接拓扑；
	 * 3. 显式 PointCoincidentConstraint 的恢复也复用同一口径，避免 create/rebuild 产生不同结果。
	 *
	 * @param sourceObject 源实体对象。
	 * @returns 可参与端点拓扑恢复的点对象集合。
	 */
	private resolveEndpointObjects(sourceObject: Object3D): SketchPointObject[] {
		if (isSketchLineObject(sourceObject)) {
			return [sourceObject.startPoint, sourceObject.endPoint];
		}

		if (isSketchArcObject(sourceObject) || isSketchEllipseObject(sourceObject)) {
			return [sourceObject.getStartPoint(), sourceObject.getEndPoint()];
		}

		if (isSketchSplineObject(sourceObject) || isSketchStyleSplineObject(sourceObject)) {
			const controlPoints = sourceObject.getControlPoints();
			const startPoint = controlPoints[0];
			const endPoint = controlPoints[controlPoints.length - 1];
			if (!startPoint || !endPoint) {
				return [];
			}
			return [startPoint, endPoint];
		}

		return [];
	}

	/**
	 * 依据源锚点集合恢复目标点重合约束。
	 *
	 * @param sourceAnchorIds 源锚点 id 集合。
	 * @param targetAnchorBindings 目标锚点绑定表。
	 * @param restoredConstraintIds 已恢复约束 id 集合。
	 * @param restoredAnchorGroupKeys 已恢复锚点分组 key。
	 */
	private restoreCoincidentByAnchorIds(
		sourceAnchorIds: string[],
		targetAnchorBindings: TargetAnchorBindings,
		restoredConstraintIds: number[],
		restoredAnchorGroupKeys: Set<string>
	): void {
		const groupKey = [...sourceAnchorIds].sort().join('::');
		if (restoredAnchorGroupKeys.has(groupKey)) {
			return;
		}

		const uniqueTargetPoints = Array.from(
			new Map(
				sourceAnchorIds.flatMap(sourceAnchorId => targetAnchorBindings.get(sourceAnchorId) ?? []).map(targetPoint => [targetPoint.uuid, targetPoint])
			).values()
		);
		if (uniqueTargetPoints.length <= 1) {
			return;
		}

		const reusableConstraint = this.findReusableCoincidentConstraint(uniqueTargetPoints);
		const coincidentConstraint = reusableConstraint ?? new PointCoincidentConstraint();
		if (!reusableConstraint) {
			uniqueTargetPoints.forEach(targetPoint => {
				targetPoint.constraints.addRelation(coincidentConstraint);
			});
			restoredConstraintIds.push(coincidentConstraint.id);
		}
		/**
		 * 恢复阶段只建立共享重合关系；点位由调用方在完整目标草图参数岛中统一求解，
		 * 避免此处先把第一个目标点当作隐式锚点直接传播。
		 */

		restoredAnchorGroupKeys.add(groupKey);
	}

	/**
	 * 查找是否已存在与当前目标点集合完全一致的重合约束。
	 * 说明：
	 * 1. rebuild 会多次重复进入该恢复入口；
	 * 2. 若每次都新建 PointCoincidentConstraint，会把同一批目标点反复绑定，导致约束集合膨胀；
	 * 3. 因此这里优先复用已有同目标集合约束，只执行一次重新对齐。
	 *
	 * @param targetPoints 目标点集合。
	 * @returns 命中的已存在重合约束；不存在时返回 null。
	 */
	private findReusableCoincidentConstraint(targetPoints: SketchPointObject[]): PointCoincidentConstraint | null {
		const targetPointIds = new Set(targetPoints.map(targetPoint => targetPoint.uuid));
		const checkedConstraintIds = new Set<number>();

		for (const targetPoint of targetPoints) {
			for (const constraint of targetPoint.constraints.getRelations()) {
				if (!isPointCoincidentConstraint(constraint) || checkedConstraintIds.has(constraint.id)) {
					continue;
				}

				checkedConstraintIds.add(constraint.id);
				const candidateTargetIds = Array.from(new Set(constraint.getTargetsSnapshot().map(candidatePoint => candidatePoint.uuid)));
				if (candidateTargetIds.length !== targetPointIds.size) {
					continue;
				}
				if (candidateTargetIds.every(candidateTargetId => targetPointIds.has(candidateTargetId))) {
					return constraint;
				}
			}
		}

		return null;
	}
}
